import React, { useState } from 'react';
import dubaiPoliceLogo from '../../assets/dubai-police-logo.png';

const ContactFormScreen = ({ onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="h-screen relative overflow-hidden bg-gradient-to-b from-white via-[#F6FBF9] to-[#E4EFEA]">
      {/* Vertical spine */}
      <div className="forensic-spine" />

      {/* Background layers */}
      <div className="forensic-grid-overlay" />
      <div className="forensic-map-overlay opacity-[0.05]" />
      <div className="forensic-circuit-lines" />

      {/* Geometric blocks */}
      <div className="forensic-block forensic-block-2 opacity-50" />
      <div className="forensic-corner-tl" />

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Header */}
        <header className="flex justify-between items-center px-8 py-4 flex-shrink-0">
          <div className="pl-4">
            <h1 className="text-xl font-bold tracking-[0.1em] uppercase text-[#1A1A1A]">
              Contact Information
            </h1>
            <p className="text-xs tracking-[0.15em] uppercase text-[#6B7770] font-arabic">
              معلومات التواصل
            </p>
          </div>
          <img src={dubaiPoliceLogo} alt="Dubai Police" className="h-12 w-auto" />
        </header>

        {/* Header line */}
        <div className="h-[2px] mx-8 bg-gradient-to-r from-[#0e734b] via-[#0e734b] to-transparent flex-shrink-0" style={{ backgroundSize: '40% 100%', backgroundRepeat: 'no-repeat' }} />

        {/* Form */}
        <main className="flex-1 flex items-center justify-center px-8 py-8">
          <form onSubmit={handleSubmit} className="w-full max-w-lg">
            <div className="forensic-card p-10 space-y-7">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-[0.1em]">
                  Name <span className="text-[#0e734b]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-5 py-4 border-2 rounded-sharp bg-white text-[#1A1A1A] text-base transition-colors ${
                    errors.name
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-[#D1DDD7] focus:border-[#0e734b]'
                  } focus:outline-none`}
                  placeholder="Enter your name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-2 font-medium">{errors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-[0.1em]">
                  Email <span className="text-[#0e734b]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-5 py-4 border-2 rounded-sharp bg-white text-[#1A1A1A] text-base transition-colors ${
                    errors.email
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-[#D1DDD7] focus:border-[#0e734b]'
                  } focus:outline-none`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2 font-medium">{errors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-[0.1em]">
                  Phone <span className="text-[#0e734b]">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-5 py-4 border-2 rounded-sharp bg-white text-[#1A1A1A] text-base transition-colors ${
                    errors.phone
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-[#D1DDD7] focus:border-[#0e734b]'
                  } focus:outline-none`}
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-2 font-medium">{errors.phone}</p>
                )}
              </div>

              {/* Buttons - Stacked vertically */}
              <div className="flex flex-col gap-3 pt-6">
                {/* Primary Continue Button - Full width, prominent */}
                <button
                  type="submit"
                  className="forensic-btn-primary w-full px-8 py-4 text-lg flex items-center justify-center gap-3"
                >
                  <span className="font-title uppercase font-bold">Continue</span>
                  <span className="text-white/60">|</span>
                  <span className="font-arabic font-bold">متابعة</span>
                  <span className="text-xl">→</span>
                </button>

                {/* Secondary Back Button - Smaller, less prominent */}
                <button
                  type="button"
                  onClick={onBack}
                  className="forensic-btn-secondary px-6 py-3 text-sm flex items-center justify-center gap-2"
                >
                  <span>←</span>
                  <span className="font-title uppercase">Back</span>
                  <span className="opacity-60">|</span>
                  <span className="font-arabic">رجوع</span>
                </button>
              </div>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default ContactFormScreen;
